import { useAtom } from "jotai";
import { useEffect } from "react";
import { useToasts } from "react-toast-notifications";
import { MAX_STREAMING_USERS } from "../consts";
import {
  audioStreamsAtom,
  currentSessionIdAtom,
  currentSpaceAtom,
  roomAtom,
  videoStreamsAtom,
} from "../jotai/atoms";
import { SessionId } from "../types";
import { useNetworkStatusToast } from "../utils/useNetworkStatusToast";
import { AgoraRoom, AgoraService } from "./AgoraService";
import { NetworkStatus, ParticipantsListener } from "./RoomService";
import { useAgoraDualStream } from "./useAgoraDualStream";
import { DeviceControl, useDeviceControl } from "./useDeviceControl";

export function useAV(): { audio: DeviceControl; video: DeviceControl } {
  const [space] = useAtom(currentSpaceAtom);
  const [sessionId] = useAtom(currentSessionIdAtom);
  const [room, setRoom] = useAtom(roomAtom);
  const [, setAudioStreams] = useAtom(audioStreamsAtom);
  const [videoStreams, setVideoStreams] = useAtom(videoStreamsAtom);
  const { addToast } = useToasts();
  const { networkIssueToast, slowNetworkToast } = useNetworkStatusToast();

  useEffect(() => {
    if (sessionId && space && !room) {
      AgoraService.join(space.id, sessionId)
        .then((room) => {
          setRoom(room);
        })
        .catch((e) => {
          console.error(e);
        });
      // DailyService.join(roomId, sessionId)
      //   .then((room) => {
      //     setRoom(room);
      //   })
      //   .catch((e) => {
      //     console.error(e);
      //   });
    }

    return () => {
      if (room) {
        room.leave();
        setRoom(null);
      }
    };
  }, [space, room, sessionId, setRoom]);

  useAgoraDualStream(room as AgoraRoom);

  useEffect(() => {
    if (!room) return;

    const onParticipants: ParticipantsListener = (participants) => {
      const audios: Record<SessionId, MediaStreamTrack> = {};
      const videos: Record<SessionId, MediaStreamTrack> = {};

      Object.values(participants).forEach((p) => {
        if (p.audio) {
          audios[p.id] = p.audio;
        }
        if (p.video) {
          videos[p.id] = p.video;
        }
      });

      setAudioStreams(audios);
      setVideoStreams(videos);
    };

    const onNetworkStatus = (status: NetworkStatus) => {
      if (status === "poor") {
        slowNetworkToast();
      }
      if (status === "disconnected") {
        networkIssueToast();
      }
    };

    room.on("participants", onParticipants);
    room.on("network-status", onNetworkStatus);

    return () => {
      room.off("participants", onParticipants);
      room.off("network-status", onNetworkStatus);
    };
  }, [
    room,
    setAudioStreams,
    setVideoStreams,
    networkIssueToast,
    slowNetworkToast,
  ]);

  useEffect(() => {
    if (Object.values(videoStreams).length > MAX_STREAMING_USERS) {
      addToast(
        "Too many people are streaming videos at once, consider switching to audio-only."
      );
    }
  }, [videoStreams, addToast]);

  const audio = useDeviceControl("audio", room);
  const video = useDeviceControl("video", room);

  return { audio, video };
}
